/**
 * MODULE: _shared/lib/frontend/api/taskApi.ts
 * PURPOSE: Tasks API client - CRUD operations for task management via data-management-service
 * 
 * DEPENDENCIES:
 *  - axios: HTTP client
 *  - ./config: API_CONFIG, getAuthHeaders
 * 
 * PUBLIC API:
 *  - Task: TypeScript interface for task records
 *  - taskApi.list(params): List tasks with filters
 *  - taskApi.get(taskId): Get single task
 *  - taskApi.create(task): Create new task
 *  - taskApi.update(taskId, updates): Update task
 *  - taskApi.delete(taskId): Delete task
 * 
 * SIDE EFFECTS:
 *  - HTTP requests to /api/v1/tasks endpoints
 *  - Reads JWT token from localStorage
 * 
 * AGENT SAFETY: RESTRICTED
 * FILE SIZE: 145 / 150 lines
 * RECENT: 2026-04-17 [2.2.497] — Task model includes scheduling fields (**`estimated_duration_mins`**, **`scheduling_status`**, **`scheduled_event_id`**, **`auto_schedule`**, **`priority_score`**) aligned with data-management API.
 *
 * See: /README.md#documentation | /ARCHITECTURE.md | /_shared/lib/frontend/api/README.md
 */

/**
 * List Widget API Integration
 * Connects to data-management-service via API Gateway
 * Consolidated service (Jan 2026): task, contact, note, file, metric, notification, search
 */

import axios from 'axios';
import { API_CONFIG, getAuthHeaders } from './config';

const API_BASE = API_CONFIG.baseURL;

export interface Task {
  id: string;
  tenant_id: string;
  user_id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'done' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  due_date?: string;
  completed: boolean;
  completed_at?: string;
  tags: string[];
  assignee_id?: string;
  parent_task_id?: string;
  created_by?: string;
  created_by_ai: boolean;
  ai_context?: string;
  estimated_duration_mins?: number | null;
  scheduled_event_id?: string | null;
  scheduling_status?: string;
  auto_schedule?: boolean;
  priority_score?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateTaskRequest {
  title: string;
  description?: string;
  status?: 'todo' | 'in_progress' | 'done' | 'cancelled';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  due_date?: string;
  tags?: string[];
  assignee_id?: string;
  parent_task_id?: string;
  created_by_ai?: boolean;
  ai_context?: string;
  estimated_duration_mins?: number | null;
  auto_schedule?: boolean;
  priority_score?: number;
}

// Using centralized getAuthHeaders from config.ts

export const taskApi = {
  // List tasks
  list: async (params?: {
    status?: string;
    priority?: string;
    assignee_id?: string;
    scheduling_status?: string;
    has_due_date?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<Task[]> => {
    const response = await axios.get(`${API_BASE}/api/v1/tasks`, {
      params,
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Get single task
  get: async (taskId: string): Promise<Task> => {
    const response = await axios.get(`${API_BASE}/api/v1/tasks/${taskId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Create task
  create: async (task: CreateTaskRequest): Promise<Task> => {
    const response = await axios.post(
      `${API_BASE}/api/v1/tasks`,
      task,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Update task
  update: async (taskId: string, updates: Partial<CreateTaskRequest>): Promise<Task> => {
    const response = await axios.put(
      `${API_BASE}/api/v1/tasks/${taskId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Delete task
  delete: async (taskId: string): Promise<void> => {
    await axios.delete(`${API_BASE}/api/v1/tasks/${taskId}`, {
      headers: getAuthHeaders()
    });
  },

  // Update task status
  updateStatus: async (taskId: string, status: 'todo' | 'in_progress' | 'done' | 'cancelled'): Promise<Task> => {
    const response = await axios.patch(
      `${API_BASE}/api/v1/tasks/${taskId}/status`,
      { status },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }
};

